import React from "react";
import { Card, Tag, Space, Typography, Progress, Tooltip } from "antd";
import {
  ThunderboltOutlined,
  PrinterOutlined,
  HighlightOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  LockOutlined,
  ClusterOutlined,
} from "@ant-design/icons";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import moment from "moment";

const { Text, Paragraph } = Typography;

const priorityColors = {
  urgent: "#ff4d4f",
  high: "#fa8c16",
  normal: "#1890ff",
  low: "#8c8c8c",
};

const fulfillmentTags = {
  fulfilled: { color: "green", icon: <CheckCircleOutlined />, label: "Material OK" },
  partial: { color: "orange", icon: <InboxOutlined />, label: "Partial Material" },
  pending: { color: "red", icon: <InboxOutlined />, label: "Material Pending" },
};

const KanbanCard = ({ item, isDragging, onCardClick, isLocked }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: item.id, disabled: isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortableDragging ? 0.5 : 1,
  };

  // Delivery date helpers
  const expectedDelivery = item.expected_delivery || item.order?.expected_delivery;
  const isOverdue = expectedDelivery
    ? moment(expectedDelivery).isBefore(moment(), "day")
    : false;
  const daysRemaining = expectedDelivery
    ? moment(expectedDelivery).diff(moment(), "days")
    : null;

  // Production progress
  const productionQty = Number(item.production_quantity) || 0;
  const completedQty = Number(item.completed_quantity) || 0;
  const progressPct =
    productionQty > 0 ? Math.round((completedQty / productionQty) * 100) : 0;

  const priority = item.priority || item.order?.priority || "normal";
  const fulfillment = item.fulfillment_status;
  const fTag = fulfillmentTags[fulfillment];

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(isLocked ? {} : listeners)}
      className={`kanban-card ${isDragging ? "kanban-card--dragging" : ""}`}
      onPointerUp={(e) => {
        if (!isSortableDragging && onCardClick) {
          onCardClick(item);
        }
      }}
    >
      <Card
        size="small"
        bordered
        style={{
          borderLeft: `3px solid ${priorityColors[priority] || priorityColors.normal}`,
          cursor: isLocked ? "not-allowed" : "pointer",
          opacity: isLocked ? 0.8 : 1,
        }}
      >
        {/* Header: job number + priority + lock icon */}
        <div className="kanban-card-header">
          <Text strong style={{ fontSize: 12, color: "#1890ff" }}>
            {item.job_number}
          </Text>
          <Space size={4}>
            {priority === "urgent" && (
              <ThunderboltOutlined style={{ color: "#ff4d4f" }} />
            )}
            {priority === "high" && (
              <ThunderboltOutlined style={{ color: "#fa8c16" }} />
            )}
            {isLocked && (
              <Tooltip title={`Part of batch ${item.batch_number} (locked)`}>
                <LockOutlined style={{ color: "#8c8c8c", fontSize: 11 }} />
              </Tooltip>
            )}
          </Space>
        </div>

        {/* Batch badge */}
        {item.batch_number && (
          <div style={{ marginBottom: 3 }}>
            <Tag
              icon={<ClusterOutlined />}
              color={isLocked ? "default" : "warning"}
              style={{ fontSize: 10 }}
            >
              {item.batch_number}
            </Tag>
          </div>
        )}

        {/* Order reference */}
        {item.order?.order_number && (
          <Text
            type="secondary"
            style={{ fontSize: 11, display: "block", marginBottom: 2 }}
          >
            {item.order.order_number}
          </Text>
        )}

        {/* Customer name */}
        {item.order?.customer?.name && (
          <Text strong style={{ fontSize: 11, display: "block", marginBottom: 2, color: "#595959" }}>
            {item.order.customer.name}
          </Text>
        )}

        {/* Item name */}
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{ marginBottom: 4, marginTop: 2, fontSize: 13 }}
        >
          {item.item_name}
        </Paragraph>

        {/* Box details */}
        <div style={{ fontSize: 11, color: "#595959", marginBottom: 4, lineHeight: "18px" }}>
          {item.length && item.width ? (
            <div>📦 {item.length}×{item.width}{item.height ? `×${item.height}` : ""} {item.unit_type || ""}</div>
          ) : null}
          {item.subcategory && <div>📋 {item.subcategory}</div>}
          {item.order?.customer_po_number && <div>🧾 PO: {item.order.customer_po_number}</div>}
        </div>

        {/* Specs */}
        <Space size={4} wrap style={{ marginBottom: 6 }}>
          {item.ply && (
            <Tag style={{ fontSize: 10 }}>{item.ply} Ply</Tag>
          )}
          {item.roll_size > 0 && (
            <Tag style={{ fontSize: 10 }}>RS {item.roll_size}"</Tag>
          )}
          {item.cutting_size > 0 && (
            <Tag style={{ fontSize: 10 }}>CS {item.cutting_size}"</Tag>
          )}
        </Space>

        {/* Quantity progress */}
        <div style={{ marginBottom: 6 }}>
          <Text style={{ fontSize: 11 }}>
            Qty: {completedQty.toLocaleString("en-IN")} /{" "}
            {productionQty.toLocaleString("en-IN")}
          </Text>
          <Progress
            percent={progressPct}
            size="small"
            showInfo={false}
            status={progressPct === 100 ? "success" : "active"}
          />
        </div>

        {/* Material fulfillment */}
        {fTag && (
          <div style={{ marginBottom: 6 }}>
            <Tag
              color={fTag.color}
              icon={fTag.icon}
              style={{ fontSize: 10 }}
            >
              {fTag.label}
            </Tag>
          </div>
        )}

        {/* Footer: delivery date */}
        <div className="kanban-card-footer">
          {expectedDelivery ? (
            <Tooltip title={moment(expectedDelivery).format("DD MMM YYYY")}>
              <Space size={4}>
                <ClockCircleOutlined style={{ fontSize: 11 }} />
                <Text
                  type={
                    isOverdue
                      ? "danger"
                      : daysRemaining !== null && daysRemaining <= 2
                      ? "warning"
                      : "secondary"
                  }
                  style={{ fontSize: 11 }}
                >
                  {isOverdue
                    ? "Overdue"
                    : daysRemaining === 0
                    ? "Today"
                    : `${daysRemaining}d left`}
                </Text>
              </Space>
            </Tooltip>
          ) : (
            <span />
          )}

          {/* Priority tag */}
          <Tag
            color={priorityColors[priority]}
            style={{ fontSize: 10, textTransform: "capitalize" }}
          >
            {priority}
          </Tag>
        </div>
      </Card>
    </div>
  );
};

export default KanbanCard;
